import './Autocomplete.scss';
import React from 'react';
import { Icon } from 'antd';
import { FormattedMessage } from 'react-intl';
import { IFormattedText } from 'Components/Shared/FormattedText/FormattedText';
import OutsideClickHandler from 'Components/Shared/OutsideClickHandler/OutsideClickHandler';

interface IProps {
  defaultValue?: string;
  options: string[];
  iconType?: string
  triggerStyle?: string;
  placeholder?: IFormattedText;
  onSelect: (value: string) => void;
  onChange?: (value: string) => void;
}

interface IState {
  inputValue: string;
  visibleOptions: string[];
  markedOptionIndex: number;
}

export class Autocomplete extends React.PureComponent<IProps, IState> {
  static defaultProps: Partial<IProps> = {
    defaultValue: '',
  }

  constructor(props: IProps) {
    super(props);

    this.state = {
      inputValue: this.props.defaultValue,
      visibleOptions: [],
      markedOptionIndex: 0,
    }
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.options !== prevProps.options) {
      this.updateVisibleOptions();
    }
  }
  
  onClear = () => {
    this.onSelect(null);
  }

  onSelect = (option: string) => {
    const newOption = option && option.length !== 0 ? option : null;
    this.props.onSelect(newOption);
    this.setState({ inputValue: newOption, visibleOptions: [] });
  }
  
  onKeyUp = (e: React.KeyboardEvent) => {
    switch (e.keyCode) {
      case 13:
        this.handleEnterClick();
        break;
      case 38:
        this.handleUpClick();
        break;
      case 40:
        this.handleDownClick();
        break;
    }
  }

  handleEnterClick = () => {
    const { visibleOptions, inputValue } = this.state;

    if (visibleOptions.length) {
      this.onSelect(visibleOptions[this.state.markedOptionIndex]);
    } else {
      this.onSelect(inputValue);
    }
  }

  handleUpClick = () => {
    const { markedOptionIndex } = this.state;
    if (!(markedOptionIndex <= 0)) {
      this.setState({ markedOptionIndex: markedOptionIndex - 1 });
    }
  }

  handleDownClick = () => {
    const { markedOptionIndex, visibleOptions } = this.state;
    if (!(markedOptionIndex >= visibleOptions.length - 1)) {
      this.setState({ markedOptionIndex: markedOptionIndex + 1 });
    }
  }

  onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;

    if (this.props.onChange) {
      this.props.onChange(newInputValue);
    }

    if (newInputValue === '') {
      this.clearInput();
      return;
    }

    const { options } = this.props;
    const newVisibleOptions = options.filter(o => {
      return o.toLowerCase().startsWith(newInputValue.toLowerCase());
    });

    const prevOptionIndex = this.state.markedOptionIndex;
    let nextOptionIndex = 0;
    let nextMarkedOption = '$';

    if (prevOptionIndex <= newVisibleOptions.length - 1) {
      nextMarkedOption = newVisibleOptions[prevOptionIndex];
    }
    if (nextMarkedOption.toLowerCase().startsWith(newInputValue.toLowerCase())) {
      nextOptionIndex = prevOptionIndex;
    }

    this.setState({ 
      inputValue: newInputValue, 
      visibleOptions: newVisibleOptions, 
      markedOptionIndex: nextOptionIndex,
    });
  }

  clearInput = () => {
    this.setState({ 
      inputValue: '', 
      visibleOptions: [], 
      markedOptionIndex: 0,
    });
  }

  updateVisibleOptions = () => {
    const { options } = this.props;
    const { inputValue } = this.state;

    if (options && inputValue) {
      const visibleOptions = options.filter(o => {
        return o.toLowerCase().startsWith(inputValue.toLowerCase());
      });
      this.setState({ visibleOptions });
    }
  }

  handleOutsideClick = () => {
    this.setState({ visibleOptions: [] });
  }

  render() {
    const { placeholder, defaultValue, triggerStyle, iconType } = this.props;
    const { visibleOptions, inputValue, markedOptionIndex } = this.state;
    const value = inputValue ? inputValue : '';

    return (
      <OutsideClickHandler style='autocomplete' onOutsideClick={ this.handleOutsideClick }>
        <div className={ triggerStyle ? triggerStyle : undefined }>
          { iconType && 
            <Icon type={ iconType } /> 
          }
          { placeholder ? 
            <FormattedMessage 
              id={ placeholder.formattedName } 
              defaultMessage={ placeholder.displayName }>
              {
                (placeholderString: string) =>
                <input 
                  type="text" 
                  value={ value }
                  onChange={ this.onChange }
                  onKeyUp={ this.onKeyUp }
                  placeholder={ placeholderString }
                />
              }
            </FormattedMessage> :
            <input 
              type="text" 
              value={ value }
              onChange={ this.onChange }
              onKeyUp={ this.onKeyUp }
            />
          }
          { defaultValue && 
            <Icon type='close' onClick={ this.onClear } />
          }
        </div>
        { visibleOptions && visibleOptions.length > 0 &&
          <ul className='options'>
            { visibleOptions.map((o, i) => 
              <li 
                key={ o } 
                className={ i === markedOptionIndex ? 'marked' : ''  } 
                onClick={ () => this.onSelect(o) }>
                { o }
              </li>) 
            }
          </ul>
        }
      </OutsideClickHandler>
    );
  }
};

export default Autocomplete;


// WEBPACK FOOTER //
// ./src/components/Shared/Autocomplete/Autocomplete.tsx